import React, { useState, useEffect } from 'react';
import {
    Server,
    Globe,
    Zap,
    Trash2,
    Plus,
    Unplug,
    Edit2,
    Shield,
    User,
    Hash,
    ShieldCheck,
    Search,
    SlidersHorizontal,
    ArrowUpDown,
    ChevronDown,
    FileText,
    Check,
    AlertCircle,
    CheckCircle,
    X
} from 'lucide-react';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';
import onPremiseLogo from '../../assets/logos/on-premises-logo.svg';
import './DMSConnections.css';

import { API_BASE_URL } from '../../config';

const AnimatedCounter = ({ value, duration = 1000 }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
        let startTime = null;
        const startValue = count;
        const endValue = value;
        const diff = endValue - startValue;
        
        if (diff === 0) return;
        
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeProgress = progress * (2 - progress);
            setCount(Math.floor(startValue + diff * easeProgress));
            if (progress < 1) {
                window.requestAnimationFrame(step);
            } else {
                setCount(endValue);
            }
        };
        window.requestAnimationFrame(step);
    }, [value, duration]);
    
    return <span>{count}</span>;
};

const maskHostEndpoint = (host) => {
    if (!host) return '—';
    const str = String(host).trim();
    if (!str) return '—';

    // IPv4 Address (e.g. 13.127.60.98 -> 13.127.•••.•••)
    const ipRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const matchIp = str.match(ipRegex);
    if (matchIp) {
        return `${matchIp[1]}.${matchIp[2]}.•••.•••`;
    }

    // Fully Qualified Domain Name (e.g. aws-db.c3qaw8y262ok.ap-south-1.rds.amazonaws.com)
    const parts = str.split('.');
    if (parts.length > 2) {
        const first = parts[0];
        const prefix = first.length > 2 ? first.slice(0, 2) : first;
        const lastTwo = parts.slice(-2).join('.');
        return `${prefix}••••••••.${lastTwo}`;
    }

    // Short hostnames (e.g. erwerwer -> er••••••••, wrew -> wr••••••••)
    if (str.length <= 3) return '••••••••';
    return str.slice(0, 2) + '••••••••';
};

const DMSConnections = ({ connections, setShowConnectionModal, onEdit, onRefresh, onDeleteConnection, setNotify, currentTab = 'active', setCurrentTab }) => {
    const [testingConnection, setTestingConnection] = useState(null);
    const [testResultModal, setTestResultModal] = useState(null);

    // Auto-refresh polling every 60s when this component is mounted
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (typeof onRefresh === 'function') {
                onRefresh();
            }
        }, 60000);
        return () => clearInterval(intervalId);
    }, [onRefresh]);

    // Auto-test connections on mount / when connections list is loaded if status is unknown or awaiting
    const autoTestedRef = React.useRef(new Set());
    useEffect(() => {
        if (connections && connections.length > 0) {
            connections.forEach(conn => {
                if (conn.id && !autoTestedRef.current.has(conn.id)) {
                    const isDraft = conn.options && (typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options)?.is_draft === true;
                    if (!isDraft && (!conn.health_status || conn.health_status === 'unknown')) {
                        autoTestedRef.current.add(conn.id);
                        handleTestConnection(conn.id);
                    }
                }
            });
        }
    }, [connections]);

    const handleTestAllConnections = async () => {
        if (!connections || connections.length === 0) return;
        for (const conn of connections) {
            await handleTestConnection(conn.id);
        }
    };


    // Transition state
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Toolbar States
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [sortBy, setSortBy] = useState('recently_created');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

    // Close dropdowns on outside clicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.toolbar-dropdown-container')) {
                setIsFilterDropdownOpen(false);
                setIsSortDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTestConnection = async (connectionId) => {
        setTestingConnection(connectionId);
        try {
            const connection = connections.find(c => c.id === connectionId);
            if (!connection) return;

            const response = await fetch(`${API_BASE_URL}/api/connections/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...connection,
                    port: parseInt(connection.port, 10)
                })
            });

            const data = await response.json();
            const rawError = data.error || data.details || (Array.isArray(data.errors) && data.errors.length > 0 ? data.errors.join('; ') : '');

            if (data.success) {
                setTestResultModal({
                    connection,
                    success: true,
                    message: `Connected successfully to ${connection.host}:${connection.port}`
                });
            } else {
                let cleanError = rawError || 'Connection test failed.';
                if (rawError.includes('ENOTFOUND') || rawError.includes('getaddrinfo')) {
                    cleanError = `Host "${connection.host}" could not be resolved. Please check the hostname / IP address.`;
                } else if (rawError.includes('ECONNREFUSED')) {
                    cleanError = `Connection refused at ${connection.host}:${connection.port}. Ensure the database is running and port ${connection.port} is open.`;
                } else if (rawError.includes('ETIMEDOUT') || rawError.includes('timeout')) {
                    cleanError = `Connection timed out reaching ${connection.host}:${connection.port}. Please check network / firewall settings.`;
                } else if (rawError.includes('password authentication failed') || rawError.includes('Access denied for user')) {
                    cleanError = `Authentication failed for user "${connection.username}". Please check username and password.`;
                }

                setTestResultModal({
                    connection,
                    success: false,
                    message: cleanError,
                    suggestions: data.suggestions || [
                        'Ensure the database host / IP address is correct and publicly reachable.',
                        'Check security group and firewall rules to allow inbound traffic on the database port.',
                        'Tip: If this database is in a private network, consider using Managed SSH Tunneling or Dedicated VPN Tunnel.'
                    ]
                });
            }
            if (typeof onRefresh === 'function') onRefresh();
        } catch (error) {
            setTestResultModal({
                connection: connections.find(c => c.id === connectionId) || { name: 'Database Connection' },
                success: false,
                message: 'Unable to reach backend service to test connection.',
                suggestions: ['Verify backend service status and network connectivity.']
            });
        } finally {
            setTestingConnection(null);
        }
    };

    const handleDeleteClick = (connection) => {
        if (typeof onDeleteConnection === 'function') {
            onDeleteConnection(connection);
        }
    };

    const handleTabChange = (tab) => {
        if (tab === currentTab) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentTab(tab);
            setSearchQuery('');
            setSelectedFilter('all');
            setIsTransitioning(false);
        }, 300);
    };

    // Active Connections
    const activeConnections = connections ? connections.filter(conn => {
        if (!conn.options) return true;
        try {
            const opts = typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options;
            return !opts || (opts.is_draft !== true && opts.isDraft !== true);
        } catch (e) {
            return true;
        }
    }) : [];

    // Filter & Sort Connections
    const getFilteredAndSorted = (list) => {
        let result = [...list];

        // 1. Search Query Filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(conn => {
                const status = conn.health_status || 'unknown';
                const isPrivate = conn.connectivity_mode === 'private';
                return (
                    conn.name?.toLowerCase().includes(q) ||
                    conn.host?.toLowerCase().includes(q) ||
                    conn.database_engine?.toLowerCase().includes(q) ||
                    conn.type?.toLowerCase().includes(q) ||
                    conn.cloud_provider?.toLowerCase().includes(q) ||
                    (isPrivate ? 'private' : 'public').includes(q) ||
                    status.includes(q) || (status === 'connected' && 'success'.includes(q))
                );
            });
        }

        // 2. Dropdown Category Filter
        if (selectedFilter !== 'all') {
            result = result.filter(conn => {
                const status = conn.health_status || 'unknown';
                switch (selectedFilter) {
                    case 'connected':
                        return status === 'connected';
                    case 'failed':
                        return status === 'failed';
                    case 'private':
                        return conn.connectivity_mode === 'private';
                    case 'public':
                        return conn.connectivity_mode === 'public';
                    case 'aws':
                        return conn.cloud_provider?.toLowerCase() === 'aws';
                    case 'gcp':
                        return conn.cloud_provider?.toLowerCase() === 'gcp';
                    default:
                        return true;
                }
            });
        }

        // 3. Sorting Logic
        result.sort((a, b) => {
            if (sortBy === 'a_z') {
                return (a.name || '').localeCompare(b.name || '');
            } else if (sortBy === 'recently_created') {
                const getCreateTime = (item) => {
                    if (item.created_at) return new Date(item.created_at).getTime();
                    return 0;
                };
                return getCreateTime(b) - getCreateTime(a);
            } else if (sortBy === 'recently_updated') {
                return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
            } else if (sortBy === 'status') {
                const statusA = a.health_status || 'unknown';
                const statusB = b.health_status || 'unknown';
                const weight = { connected: 3, failed: 2, unknown: 1 };
                return (weight[statusB] || 0) - (weight[statusA] || 0);
            }
            return 0;
        });

        return result;
    };

    const displayActiveConnections = getFilteredAndSorted(activeConnections);

    // Calculate metrics for the header
    const stats = {
        total: activeConnections.length,
        verified: activeConnections.filter(c => c.health_status === 'connected').length,
        failed: activeConnections.filter(c => c.health_status === 'failed').length
    };

    const filterOptions = [
        { value: 'all', label: 'All Connections' },
        { value: 'connected', label: 'Connected' },
        { value: 'failed', label: 'Failed' },
        { value: 'private', label: 'Private Network' },
        { value: 'public', label: 'Public Internet' },
        { value: 'aws', label: 'AWS Cloud' },
        { value: 'gcp', label: 'GCP Cloud' }
    ];

    const sortOptions = [
        { value: 'recently_created', label: 'Recently Created' },
        { value: 'recently_updated', label: 'Recently Updated' },
        { value: 'a_z', label: 'Alphabetical (A-Z)' },
        { value: 'status', label: 'Sort by Status' }
    ];

    const activeFilterLabel = filterOptions.find(o => o.value === selectedFilter)?.label || 'Filter';
    const activeSortLabel = sortOptions.find(o => o.value === sortBy)?.label || 'Sort';

    return (
        <div className="dms-page-container dms-connections-container">
            <div className="dms-page-header">
                <div className="header-title-stack">
                    <h2>Database Connections</h2>
                    <p className="header-subtitle">Manage and monitor your enterprise database ecosystem and connectivity bridges.</p>
                </div>
                <div className="header-actions-premium">
                    <div className="stats-summary-bar">
                        <div className="stat-pill">
                            <span className="stat-label">Total</span>
                            <span className="stat-count"><AnimatedCounter value={stats.total} /></span>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-pill verified">
                            <span className="stat-label">Verified</span>
                            <span className="stat-count"><AnimatedCounter value={stats.verified} /></span>
                        </div>
                        <div className="stat-pill failed">
                            <span className="stat-label">Failed</span>
                            <span className="stat-count"><AnimatedCounter value={stats.failed} /></span>
                        </div>
                    </div>
                    <button
                        className="btn-add-nexus"
                        onClick={() => setShowConnectionModal(true)}
                    >
                        <Plus size={18} />
                        <span>Add Connection</span>
                    </button>
                </div>
            </div>

            {/* Toolbar: Search, Filter, Sort */}
            <div className="connections-toolbar-premium">
                <div className="search-bar-glass-container">
                    <Search size={16} className="search-icon-dim" />
                    <input
                        type="text"
                        className="search-input-glass"
                        placeholder="Search connections..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search-btn" onClick={() => setSearchQuery('')}>&times;</button>
                    )}
                </div>

                <div className="toolbar-actions-group">
                    {/* Filter Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className={`btn-toolbar-dropdown ${selectedFilter !== 'all' ? 'active-filter' : ''}`}
                            onClick={() => {
                                setIsFilterDropdownOpen(!isFilterDropdownOpen);
                                setIsSortDropdownOpen(false);
                            }}
                        >
                            <SlidersHorizontal size={13} />
                            <span>{activeFilterLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isFilterDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isFilterDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Filter by</div>
                            <div className="dropdown-panel-options">
                                {filterOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${selectedFilter === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedFilter(opt.value);
                                            setIsFilterDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {selectedFilter === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className="btn-toolbar-dropdown"
                            onClick={() => {
                                setIsSortDropdownOpen(!isSortDropdownOpen);
                                setIsFilterDropdownOpen(false);
                            }}
                        >
                            <ArrowUpDown size={13} />
                            <span>{activeSortLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isSortDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isSortDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Sort by</div>
                            <div className="dropdown-panel-options">
                                {sortOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${sortBy === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSortBy(opt.value);
                                            setIsSortDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {sortBy === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area Grid */}
            <div className={`dms-connections-grid ${isTransitioning ? 'transition-blur' : ''}`}>
                {activeConnections.length > 0 ? (
                    displayActiveConnections.length > 0 ? (
                        displayActiveConnections.map((connection, index) => (
                            <div
                                key={connection.id}
                                className={`dms-conn-card role-${connection.role || 'source'} ${connection.health_status === 'connected' ? 'success' : connection.health_status || 'unknown'} ${testingConnection === connection.id ? 'checking' : ''}`}
                                style={{
                                    animationDelay: `${index * 0.08}s`,
                                    visibility: 'visible'
                                }}
                            >
                                <div className="card-top-row">
                                    <div className={`role-badge-pill ${connection.role || 'source'}`}>
                                        {connection.role?.toUpperCase() || 'SOURCE'}
                                    </div>
                                    <div className="connectivity-chips-group">
                                        <div className="connectivity-label">
                                            {connection.connectivity_mode === 'public' ? (
                                                <><Globe size={11} /> PUBLIC</>
                                            ) : (
                                                <><Shield size={11} /> PRIVATE</>
                                            )}
                                        </div>
                                        <div className={`status-badge-pill ${testingConnection === connection.id ? 'checking' : (connection.health_status === 'connected' ? 'success' : connection.health_status || 'unknown')}`}>
                                            {testingConnection === connection.id ? 'CHECKING...' : 
                                             connection.health_status === 'connected' ? 'CONNECTED' : 
                                             connection.health_status === 'failed' ? 'FAILED' : 'AWAITING'}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-identity-header">
                                    <div className="provider-avatar-glass">
                                        <ProviderLogo provider={connection.cloud_provider} size={36} />
                                    </div>
                                    <div className="title-stack-content">
                                        <div className="primary-title-row">
                                            <h3>{connection.name}</h3>
                                        </div>
                                        <div className="subtitle-detail">
                                            {connection.database_engine || "Amazon RDS for PostgreSQL"}
                                        </div>
                                        <div className="inline-badges">
                                            {connection.region && (
                                                <span className="region-pill-min">{connection.region.toUpperCase()}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="actions-row-strip">
                                    <button
                                        className="btn-sq-action test"
                                        onClick={() => handleTestConnection(connection.id)}
                                        disabled={testingConnection === connection.id}
                                    >
                                        <Zap size={14} className={testingConnection === connection.id ? "spin-smooth" : ""} />
                                        <span>TEST</span>
                                    </button>
                                    <button
                                        className="btn-sq-action edit"
                                        onClick={() => onEdit && onEdit(connection)}
                                    >
                                        <Edit2 size={14} />
                                        <span>EDIT</span>
                                    </button>
                                    <button
                                        className="btn-sq-action delete"
                                        onClick={() => handleDeleteClick(connection)}
                                    >
                                        <Trash2 size={14} />
                                        <span>DELETE</span>
                                    </button>
                                </div>

                                <div className="inner-data-slate">
                                    <div className="slate-item-box full">
                                        <div className="slate-label-row">
                                            <Server size={12} className="slate-item-icon" />
                                            <span className="slate-label">Host Endpoint</span>
                                        </div>
                                        <span className="slate-value">{maskHostEndpoint(connection.host)}</span>
                                    </div>
                                    <div className="slate-grid-row tri">
                                        <div className="slate-item-box">
                                            <div className="slate-label-row">
                                                <Hash size={12} className="slate-item-icon" />
                                                <span className="slate-label">Port</span>
                                            </div>
                                            <span className="slate-value">{connection.port || 5432}</span>
                                        </div>
                                        <div className="slate-item-box">
                                            <div className="slate-label-row">
                                                <User size={12} className="slate-item-icon" />
                                                <span className="slate-label">User</span>
                                            </div>
                                            <span className="slate-value">{connection.username}</span>
                                        </div>
                                        <div className="slate-item-box">
                                            <div className="slate-label-row">
                                                {connection.ssl_enabled ?
                                                    <ShieldCheck size={12} className="slate-item-icon secure" /> :
                                                    <Shield size={12} className="slate-item-icon" />
                                                }
                                                <span className="slate-label">Security</span>
                                            </div>
                                            <span className={`slate-value ${connection.ssl_enabled ? 'secure-text' : ''}`}>
                                                {connection.ssl_enabled ? 'SSL Secure' : 'Standard'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer-mini">
                                    <div className="footer-meta-right">
                                        <span className="date-stamp-mini">
                                            {connection.created_at ? new Date(connection.created_at).toLocaleDateString() : new Date().toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-search-results-glass">
                            <SlidersHorizontal size={40} className="no-results-icon" />
                            <h3>No matching connections</h3>
                            <p>We couldn't find any active database connections matching your query or selected filters.</p>
                            <button className="btn-secondary-toolbar" onClick={() => {
                                setSearchQuery('');
                                setSelectedFilter('all');
                            }}>
                                Reset Filters
                            </button>
                        </div>
                    )
                ) : (
                    <div className="empty-state-card">
                        <div className="empty-state-icon">
                            <Unplug size={48} className="spin-slow" />
                        </div>
                        <h3>No database connections yet</h3>
                        <p>
                            Add your source and destination databases to start building your migration pipelines.
                        </p>
                        <button
                            className="btn-primary"
                            onClick={() => setShowConnectionModal(true)}
                            style={{ padding: '0.85rem 2.5rem', marginTop: '1rem' }}
                        >
                            <Plus size={20} />
                            Create First Connection
                        </button>
                    </div>
                )}
            </div>

            {/* ── Test Connection Result Modal Popup ── */}
            {testResultModal && (
                <div className="modal-overlay" onClick={() => setTestResultModal(null)}>
                    <div className="modal-content" style={{ maxWidth: '520px', borderRadius: 16, padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{
                            padding: '1.25rem 1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: 'var(--bg-card, rgba(15,23,42,0.6))'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Zap size={18} color="#7c3aed" />
                                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                                    Connection Test — {testResultModal.connection.name}
                                </h3>
                            </div>
                            <button className="modal-close" onClick={() => setTestResultModal(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body" style={{ padding: '1.5rem' }}>
                            <div style={{
                                padding: '1rem 1.25rem',
                                borderRadius: 12,
                                display: 'flex', flexDirection: 'column', gap: 8,
                                background: testResultModal.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                                border: `1px solid ${testResultModal.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                                color: testResultModal.success ? '#10b981' : '#ef4444',
                                marginBottom: '1.25rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: '0.95rem' }}>
                                    {testResultModal.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                                    <span>{testResultModal.success ? 'Connection Successful' : 'Connection Test Failed'}</span>
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginTop: 4, lineHeight: 1.5 }}>
                                    {testResultModal.message}
                                </div>
                            </div>

                            {!testResultModal.success && testResultModal.suggestions?.length > 0 && (
                                <div style={{ background: 'var(--bg-input, rgba(15,23,42,0.4))', padding: '1rem 1.25rem', borderRadius: 12, border: '1px solid var(--border-color)' }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>
                                        Troubleshooting Suggestions
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.825rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                        {testResultModal.suggestions.map((s, i) => (
                                            <li key={i} style={{ marginBottom: 4 }}>{s}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{
                            padding: '1rem 1.5rem',
                            borderTop: '1px solid var(--border-color)',
                            display: 'flex', justifyContent: 'flex-end', gap: 10,
                            background: 'var(--bg-card, rgba(15,23,42,0.6))'
                        }}>
                            <button className="btn-secondary" onClick={() => setTestResultModal(null)} style={{ padding: '0.5rem 1.25rem' }}>
                                Close
                            </button>
                            {!testResultModal.success && (
                                <button className="btn-primary" onClick={() => {
                                    const conn = testResultModal.connection;
                                    setTestResultModal(null);
                                    if (onEdit) onEdit(conn);
                                }} style={{ padding: '0.5rem 1.25rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Edit2 size={14} /> Edit Connection
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DMSConnections;

// ── Global Helper Components & Functions (Defined outside to prevent React unmount/remount loop) ──

const ProviderLogo = ({ provider, size = 18, className = "" }) => {
    const p = provider?.toLowerCase();
    if (p === 'aws') return <img src={awsLogo} alt="AWS" className={`${className} aws-logo-img aws-logo-theme-fix`} style={{ maxWidth: '82%', maxHeight: '82%', objectFit: 'contain' }} />;
    if (p === 'gcp') return <img src={gcpLogo} alt="GCP" className={className} style={{ maxWidth: '82%', maxHeight: '82%', objectFit: 'contain' }} />;
    if (p === 'azure') return <img src={azureLogo} alt="Azure" className={className} style={{ maxWidth: '82%', maxHeight: '82%', objectFit: 'contain' }} />;
    if (p === 'on-premise') return <img src={onPremiseLogo} alt="On-Premise" className={className} style={{ maxWidth: '82%', maxHeight: '82%', objectFit: 'contain' }} />;
    return <Globe size={size} />;
};

const getDraftProgress = (conn) => {
    let score = 0;
    let total = 5;
    if (conn.host) score++;
    if (conn.port) score++;
    if (conn.username) score++;
    if (conn.database_engine || conn.type) score++;
    if (conn.role) score++;
    const percent = Math.round((score / total) * 100);
    return { score, total, percent };
};

const getRelativeTime = (dateStr) => {
    if (!dateStr) return 'Just now';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
};

const DraftCard = ({ conn, index, onEdit, onDelete }) => {
    const incomplete = !conn.host || !conn.username || !conn.database_engine;
    const autoSaved = conn.options && (typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options).auto_saved === true;
    const { percent } = getDraftProgress(conn);

    return (
        <div
            className={`draft-conn-card role-${conn.role || 'source'}`}
            style={{ animationDelay: `${index * 0.08}s` }}
        >
            <div className="card-top-row">
                <div className={`role-badge-pill ${conn.role || 'source'}`}>
                    {conn.role?.toUpperCase() || 'SOURCE'}
                </div>
                <div className="draft-badges-group">
                    {autoSaved && (
                        <span className="badge-auto-saved">
                            <Zap size={9} /> Auto Saved
                        </span>
                    )}
                    {incomplete && (
                        <span className="badge-incomplete">
                            <AlertCircle size={9} /> Incomplete
                        </span>
                    )}
                </div>
            </div>

            <div className="card-identity-header">
                <div className="provider-avatar-glass draft-avatar">
                    <ProviderLogo provider={conn.cloud_provider} size={36} />
                </div>
                <div className="title-stack-content">
                    <div className="primary-title-row">
                        <h3>{conn.name || 'Untitled Draft'}</h3>
                    </div>
                    <div className="subtitle-detail">
                        {conn.database_engine || (conn.type ? conn.type.toUpperCase() : 'Database Connection')}
                    </div>
                    <div className="inline-badges">
                        {conn.region && (
                            <span className="region-pill-min">{conn.region.toUpperCase()}</span>
                        )}
                        <span className="draft-status-tag">DRAFT</span>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="draft-progress-container">
                <div className="draft-progress-label-row">
                    <span className="draft-progress-text">Configuration Progress</span>
                    <span className="draft-progress-percent">{percent}%</span>
                </div>
                <div className="draft-progress-track">
                    <div className="draft-progress-fill" style={{ width: `${percent}%` }}></div>
                </div>
            </div>

            <div className="actions-row-strip draft-actions">
                <button
                    className="btn-sq-action test resume"
                    onClick={() => onEdit && onEdit(conn)}
                >
                    <Zap size={14} />
                    <span>EDIT DRAFT</span>
                </button>
                <button
                    className="btn-sq-action delete"
                    onClick={() => onDelete && onDelete(conn)}
                >
                    <Trash2 size={14} />
                    <span>DELETE</span>
                </button>
            </div>

            <div className="inner-data-slate draft-slate">
                <div className="slate-item-box full">
                    <div className="slate-label-row">
                        <Server size={12} className="slate-item-icon" />
                        <span className="slate-label">Host Endpoint</span>
                    </div>
                    <span className={`slate-value ${!conn.host ? 'slate-empty' : ''}`} title={conn.host}>
                        {conn.host || '— not configured —'}
                    </span>
                </div>
                <div className="slate-grid-row tri">
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            <Hash size={12} className="slate-item-icon" />
                            <span className="slate-label">Port</span>
                        </div>
                        <span className={`slate-value ${!conn.port ? 'slate-empty' : ''}`}>
                            {conn.port || '—'}
                        </span>
                    </div>
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            <User size={12} className="slate-item-icon" />
                            <span className="slate-label">User</span>
                        </div>
                        <span className={`slate-value ${!conn.username ? 'slate-empty' : ''}`}>
                            {conn.username || '—'}
                        </span>
                    </div>
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            {conn.ssl_enabled ?
                                <ShieldCheck size={12} className="slate-item-icon secure" /> :
                                <Shield size={12} className="slate-item-icon" />
                            }
                            <span className="slate-label">SSL</span>
                        </div>
                        <span className="slate-value">
                            {conn.ssl_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="card-footer-mini">
                <div className="footer-meta-right">
                    <span className="date-stamp-mini">
                        {getRelativeTime(conn.updated_at || conn.created_at)}
                    </span>
                </div>
            </div>
        </div>
    );
};
